import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaFilter, FaSpinner, FaTimes } from 'react-icons/fa';
import CruiseHeader from './CruiseHeader';
import CruiseFooter from './CruiseFooter';
import CruiseSearchWidget from './CruiseSearchWidget';
import CruiseCta from './CruiseCta';
import CruiseFixedCallBar from './CruiseFixedCallBar';
import { useCruiseSearch } from '../../hooks/useCruiseSearch';
import { DEFAULT_FILTERS, filterResults, getPriceBounds } from '../../utils/resultFilters';
import { hasSearchQuery, SEARCH_PROMPT } from '../../utils/searchQuery';
import { CRUISE_SEARCH_BANNER } from '../../data/cruiseData';
import '../../styles/home-page.css';
import '../../styles/explore-page.css';
import '../../styles/cruise-page.css';

const CABIN_OPTIONS = ['any', 'Interior', 'Ocean View', 'Balcony', 'Suite'];

export default function CruiseResultsLayout({ renderCard }) {
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());
  const queryReady = useMemo(() => hasSearchQuery(searchParams), [searchParams]);

  const { loading, results, error, catalogInfo } = useCruiseSearch(params, {
    enabled: queryReady,
  });

  const bounds = useMemo(() => getPriceBounds(results), [results]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      minPrice: bounds.min,
      maxPrice: Math.max(bounds.max, bounds.min + 100),
    }));
  }, [bounds.min, bounds.max]);

  useEffect(() => {
    if (!showFilters) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showFilters]);

  const filtered = useMemo(() => filterResults(results, filters), [results, filters]);

  const summary = `${searchParams.get('port') || 'Port'} → ${searchParams.get('destination') || 'Region'} · ${searchParams.get('depart') || 'Flexible'}`;

  return (
    <div className="cruise-page explore-page mcr-home">
      <CruiseHeader />
      <section
        className="cruise-results-hero"
        style={{ '--cruise-results-banner': `url("${CRUISE_SEARCH_BANNER}")` }}
      >
        <div className="container">
          <Link to="/cruises" className="cruise-results-hero__back">
            <FaArrowLeft aria-hidden /> Back to cruises
          </Link>
          <div className="cruise-results-hero__row">
            <div>
              <h1>{queryReady ? 'Cruise Search Results' : 'Search Cruises'}</h1>
              <p className="cruise-results-hero__summary">{queryReady ? summary : SEARCH_PROMPT}</p>
            </div>
            {queryReady && <span className="cruise-results-hero__badge">Sample availability</span>}
          </div>
          {queryReady && catalogInfo && <p className="cruise-results-hero__catalog">{catalogInfo}</p>}
          {queryReady && error && (
            <p className="cruise-results-hero__error" role="status">
              {error}
            </p>
          )}
        </div>
      </section>

      {!queryReady ? (
        <section className="mcr-section mcr-section--soft mcr-section--compact">
          <div className="container cruise-results-prompt">
            <CruiseSearchWidget />
          </div>
        </section>
      ) : (
        <>
          <div className="container cruise-results-page">
            <aside className={`cruise-results-filters ${showFilters ? 'is-open' : ''}`}>
              <div className="cruise-results-filters__head">
                <h3>
                  <FaFilter aria-hidden /> Filters
                </h3>
                <button
                  type="button"
                  className="cruise-results-filters__close"
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                >
                  <FaTimes aria-hidden />
                </button>
              </div>

              <label className="cruise-results-filters__field">
                <span>Sort by</span>
                <select
                  value={filters.sort}
                  onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                >
                  <option value="price-asc">Price: low to high</option>
                  <option value="price-desc">Price: high to low</option>
                  <option value="nights-asc">Nights: shortest</option>
                  <option value="nights-desc">Nights: longest</option>
                </select>
              </label>

              <label className="cruise-results-filters__field">
                <span>Max price (${filters.maxPrice})</span>
                <input
                  type="range"
                  min={bounds.min}
                  max={Math.max(bounds.max, bounds.min + 100)}
                  value={filters.maxPrice}
                  onChange={(e) => setFilters((f) => ({ ...f, maxPrice: Number(e.target.value) }))}
                />
              </label>

              <label className="cruise-results-filters__field">
                <span>Cabin type</span>
                <select
                  value={filters.cabinType}
                  onChange={(e) => setFilters((f) => ({ ...f, cabinType: e.target.value }))}
                >
                  {CABIN_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c === 'any' ? 'Any cabin' : c}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cruise-results-filters__field">
                <span>Min nights ({filters.minNights || 'Any'})</span>
                <input
                  type="range"
                  min={0}
                  max={14}
                  value={filters.minNights}
                  onChange={(e) => setFilters((f) => ({ ...f, minNights: Number(e.target.value) }))}
                />
              </label>

              <button
                type="button"
                className="cruise-results-filters__apply"
                onClick={() => setShowFilters(false)}
              >
                Apply filters
              </button>

              <CruiseCta
                variant="compact"
                className="cruise-results-filters__cta"
                title="Need help with this search?"
                subtitle="Call for live cruise quotes & cabin options."
              />
            </aside>

            <div className="cruise-results-main">
              <div className="cruise-results-toolbar">
                {loading ? (
                  <p className="cruise-results-loading">
                    <FaSpinner className="cruise-results-spin" aria-hidden /> Searching cruise sailings…
                  </p>
                ) : (
                  <p>
                    <strong>{filtered.length}</strong> of {results.length} results
                  </p>
                )}
                <button
                  type="button"
                  className="cruise-results-filters-toggle"
                  onClick={() => setShowFilters(true)}
                >
                  <FaFilter aria-hidden /> Filters
                </button>
              </div>

              <div className="cruise-results-list">
                {loading ? (
                  <div className="cruise-results-skeleton">
                    {[1, 2, 3, 4].map((n) => (
                      <div key={n} className="cruise-results-skeleton__card" />
                    ))}
                  </div>
                ) : filtered.length === 0 ? (
                  <p className="cruise-results-empty">
                    No cruises match your filters. Try resetting filters or changing your sailing date.
                  </p>
                ) : (
                  filtered.map((item) => renderCard(item, searchParams))
                )}
              </div>

              <CruiseCta
                variant="compact"
                title="Found a sailing you like?"
                subtitle="Call now to hold your cabin or complete booking."
              />
            </div>
          </div>

          <section className="mcr-section mcr-section--soft mcr-section--compact">
            <div className="container">
              <h2 className="cruise-results-refine">Refine your cruise search</h2>
              <CruiseSearchWidget compact />
            </div>
          </section>
        </>
      )}

      <CruiseFooter />
      <CruiseFixedCallBar />
    </div>
  );
}
